import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import {
    OdataMeasurementToolsInstanceToken,
    OdataMeasurementToolsInterface,
} from '../../../interfaces/odata-measurement-tools.interface';
import { hydrateSlice, persistSlice, sliceHydrated } from '../../persistence';
import {
    initMeasurementTools,
    measurementToolsFailed,
    measurementToolsLoaded,
    measurementToolsLoadedFromStorage,
} from '../actions/measurement-tools.actions';
import { selectMeasurementToolsLoaded } from '../selectors/measurement-tools.selector';
import { MEASUREMENT_TOOLS_FEATURE_KEY, MeasurementToolState } from '../measurement-tools-collection.state';
import * as ConfigSelectors from '../../config/selectors/config.selector';
import { selectSelectedUser } from '../../user-master';

/**
 * Owns the measurement-tools load flow and the persistence mirror. `loadMeasurementTools$`
 * fetches reference data from OData on every `initMeasurementTools` dispatch;
 * `persistMeasurementTools$` caches the slice after each backend load;
 * `hydrateMeasurementToolsOnInit$` reads the cache back at startup so tools are available
 * offline while the backend request is in flight. Both hydrate steps are guarded on
 * {@link selectMeasurementToolsLoaded} so a slower cache read can never overwrite fresher data.
 */
@Injectable()
export class MeasurementToolsEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        @Inject(OdataMeasurementToolsInstanceToken)
        private readonly odataMeasurementToolsService: OdataMeasurementToolsInterface,
    ) {}

    public loadMeasurementTools$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initMeasurementTools),
            switchMap(() => {
                const measurementToolsDetailsEnabled = this.store.selectSignal(
                    ConfigSelectors.selectShowMeasurementToolsDetails,
                )();

                if (measurementToolsDetailsEnabled) {
                    return this.odataMeasurementToolsService.getMeasurementToolsState().pipe(
                        map((tools) => {
                            // need to sort by GID because user might receive measurement tools which are assigned to other users if the user belong to a pool
                            // A measurement equipment pool in SAP, is a pool of equipments which can be used by multiple users regardless to whom the measuring equipment is assigned
                            const sortedTools = [...tools].sort((tool) =>
                                tool.gid === this.store.selectSignal(selectSelectedUser)()?.frontId ? -1 : 0,
                            );
                            return measurementToolsLoaded({ tools: sortedTools });
                        }),
                        catchError(() => of(measurementToolsFailed())),
                    );
                } else {
                    return of(measurementToolsLoaded({ tools: [] }));
                }
            }),
        ),
    );

    public persistMeasurementTools$ = createEffect(() =>
        this.actions$.pipe(
            ofType(measurementToolsLoaded),
            map(() => persistSlice({ featureKey: MEASUREMENT_TOOLS_FEATURE_KEY })),
        ),
    );

    public hydrateMeasurementToolsOnInit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(initMeasurementTools),
            concatLatestFrom(() => this.store.select(selectMeasurementToolsLoaded)),
            filter(([, loaded]) => !loaded),
            map(() => hydrateSlice({ featureKey: MEASUREMENT_TOOLS_FEATURE_KEY })),
        ),
    );

    public applyHydratedMeasurementTools$ = createEffect(() =>
        this.actions$.pipe(
            ofType(sliceHydrated),
            filter(({ featureKey }) => featureKey === MEASUREMENT_TOOLS_FEATURE_KEY),
            concatLatestFrom(() => this.store.select(selectMeasurementToolsLoaded)),
            filter(([, loaded]) => !loaded),
            map(([{ data }]) =>
                measurementToolsLoadedFromStorage({ tools: (data as MeasurementToolState[] | undefined) ?? [] }),
            ),
        ),
    );
}
